import I18n from 'i18n-js';
import gql from 'graphql-tag';
import React, { useCallback } from 'react';
import { flowRight } from 'lodash';
import { MutationFn } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import {
  CheckoutOrderSummaryFields,
  CoreCheckoutUpdateOrderQuantity,
  CoreCheckoutApplyDiscountCode,
  CoreCheckoutSelectOrderShippingMethod,
} from '../schemas/rql';
import { useMutation } from '../lib/use_mutation_hook';
import CheckoutOrderSummaryLineItems, { CheckoutOrderSummaryLineItemsFragment } from './checkout_order_summary_line_items';
import CheckoutOrderSummaryOrderItem, { CheckoutOrderSummaryOrderItemFragment } from './checkout_order_summary_order_item';
import LoadingOverlay from '../shared/loading_overlay';
import CheckoutDiscountCodeForm from './checkout_discount_code_form';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';

type UpdateOrderQuantityMutationFn = MutationFn<CoreCheckoutUpdateOrderQuantity.Mutation, CoreCheckoutUpdateOrderQuantity.Variables>;
type ApplyCheckoutDiscountCodeMutationFn = MutationFn<
CoreCheckoutApplyDiscountCode.Mutation,
CoreCheckoutApplyDiscountCode.Variables
>;
type SelectShippingMethodMutationFn = MutationFn<
CoreCheckoutSelectOrderShippingMethod.Mutation,
CoreCheckoutSelectOrderShippingMethod.Variables
>;
type OnUpdateFinishedCallback = () => void;
type Checkout = CheckoutOrderSummaryFields.Fragment;

interface ExternalProps {
  checkout: Checkout;
  onUpdate: () => OnUpdateFinishedCallback;
  showLoading?: boolean;
}

interface MutationProps {
  updateOrderQuantity: UpdateOrderQuantityMutationFn;
  selectShippingMethod: SelectShippingMethodMutationFn;
  applyCheckoutDiscountCode: ApplyCheckoutDiscountCodeMutationFn;
}

type Props = ExternalProps & MutationProps;

export function CheckoutOrderSummary(props: Props) {
  const onOrderQuantitySelect = useOrderQuantitySelect(
    props.checkout.id,
    props.updateOrderQuantity,
    props.onUpdate,
  );

  const onShippingMethodSelect = useShippingMethodSelect(
    props.checkout.id,
    props.selectShippingMethod,
    props.onUpdate,
  );

  const singlePageCheckoutExpEnabled = useExpEnabled(experiments.SINGLE_PAGE_CHECKOUT);
  const [applyCheckoutDiscountCode, { loading, errors }] = useMutation(props.applyCheckoutDiscountCode);

  const submitDiscountCode = async (discountCode) => {
    const onFinish = props.onUpdate();
    await applyCheckoutDiscountCode({
      variables: {
        input: {
          discountCode: discountCode,
          id: props.checkout.id,
        },
      },
    });
    onFinish();
  };

  return (
    <LoadingOverlay showOverlay={props.showLoading}>
      <div className="checkout-order-summary">
        <h3 className="size-120 weight-bold mb-1">
          {I18n.t('discovery.checkout.orderSummary.title')}
        </h3>
        <ul>
          {
            props.checkout.orders.map((order) => (
              <li key={order.listingId} className="checkout-order-summary__item">
                <CheckoutOrderSummaryOrderItem
                  order={order}
                  actionsDisabled={props.showLoading}
                  onQuantitySelect={onOrderQuantitySelect}
                  onShippingMethodSelect={onShippingMethodSelect}
                />
              </li>
            ))
          }
        </ul>
        {singlePageCheckoutExpEnabled &&
          <>
            <div className="bdt-1 bd-color-primary mtb-1" />
            <CheckoutDiscountCodeForm
              submitDiscountCode={submitDiscountCode}
              selectedPaymentMethod={props.checkout.paymentMethod.type}
              disableSubmitButton={loading}
              errors={errors}
            />
          </>
        }
        <div className="bdt-1 bd-color-primary mtb-1" />
        <CheckoutOrderSummaryLineItems lineItems={props.checkout.lineItems} />
      </div>
    </LoadingOverlay>
  );
}

function useOrderQuantitySelect(
  checkoutId: string,
  updateOrderQuantityMutation: UpdateOrderQuantityMutationFn,
  onUpdate: () => OnUpdateFinishedCallback,
) {
  const [updateOrderQuantity] = useMutation(updateOrderQuantityMutation);

  const onQuantitySelect = useCallback(async ({ listingId, quantity }) => {
    const onFinish = onUpdate();
    await updateOrderQuantity({
      variables: {
        checkoutId,
        listingId,
        quantity,
      },
    });
    onFinish();
  }, [checkoutId, updateOrderQuantity, onUpdate]);

  return onQuantitySelect;
}

function useShippingMethodSelect(
  checkoutId: string,
  selectShippingMethodMutation: SelectShippingMethodMutationFn,
  onUpdate: () => OnUpdateFinishedCallback,
) {
  const [selectShippingMethod] = useMutation(selectShippingMethodMutation);

  const onShippingMethodSelect = useCallback(async ({ listingId, shippingMethodType }) => {
    const onFinish = onUpdate();
    await selectShippingMethod({
      variables: {
        input: {
          id: checkoutId,
          order: {
            listingId,
            shippingMethodType,
          },
        },
      },
    });
    onFinish();
  }, [checkoutId, selectShippingMethod, onUpdate]);

  return onShippingMethodSelect;
}

export const CheckoutOrderSummaryFragment = gql`
  fragment CheckoutOrderSummaryFields on Checkout {
    _id
    id
    orders {
      listingId
    }
    paymentMethod {
      type
    }
    ...CheckoutOrderSummaryOrderItemFields
    ...CheckoutOrderSummaryLineItemsFields
  }
  ${CheckoutOrderSummaryOrderItemFragment}
  ${CheckoutOrderSummaryLineItemsFragment}
`;

const withOrderQuantityMutation = withGraphql<
ExternalProps,
CoreCheckoutUpdateOrderQuantity.Mutation,
CoreCheckoutUpdateOrderQuantity.Variables
>(
  gql`
    mutation  Core_Checkout_UpdateOrderQuantity(
      $checkoutId: String,
      $listingId: String,
      $quantity: Int
    ) {
      editCheckoutCart(
        input: {
          id: $checkoutId
          order: {
            listingId: $listingId,
            quantity: $quantity
          }
        }
      ) {
        checkout {
          _id
          ...CheckoutOrderSummaryFields
        }
      }
    }
    ${CheckoutOrderSummaryFragment}
  `,
  {
    name: 'updateOrderQuantity',
  },
);

const withDiscountCodeMutation = withGraphql<
ExternalProps,
CoreCheckoutApplyDiscountCode.Mutation,
CoreCheckoutApplyDiscountCode.Variables
>(
  gql`
    mutation Core_Checkout_ApplyDiscountCode($input: Input_core_apimessages_ApplyCheckoutDiscountCodeRequest) {
      applyCheckoutDiscountCode(input: $input) {
        checkout {
          _id
          id
          ...CheckoutOrderSummaryFields
        }
      }
    }
    ${CheckoutOrderSummaryFragment}
  `,
  {
    name: 'applyCheckoutDiscountCode',
  },
);

const withShippingMethodMutation = withGraphql<
ExternalProps,
CoreCheckoutSelectOrderShippingMethod.Mutation,
CoreCheckoutSelectOrderShippingMethod.Variables
>(
  gql`
    mutation Core_Checkout_SelectOrderShippingMethod($input: Input_core_apimessages_CheckoutSelectOrderShippingMethodRequest) {
      selectCheckoutOrderShippingMethod(input: $input) {
        checkout {
          _id
          id
          orders {
            listingId
            shippingMethod {
              name
              type
            }
          }
          ...CheckoutOrderSummaryFields
        }
      }
    }
    ${CheckoutOrderSummaryFragment}
  `,
  {
    name: 'selectShippingMethod',
  },
);

export default flowRight([
  withOrderQuantityMutation,
  withDiscountCodeMutation,
  withShippingMethodMutation,
])(CheckoutOrderSummary);
